/*
 * AI generator — save designs to list / compare (trade & interior designer boards).
 * Stores watermarked preview + wall measurements + pricing in localStorage, and
 * syncs to the site wishlist (iWish) when available.
 */
(() => {
  const LIBRARY_KEY = 'wallprint_ai_design_library';
  const COMPARE_KEY = 'wallprint_ai_design_compare';

  const readJson = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const writeJson = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota */
    }
  };

  const newId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `design-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const CM_PER_INCH = 2.54;
  const FT2_PER_M2 = 10.7639;

  const roundDisp = (unit, value) =>
    unit === 'imperial' ? Math.round(value * 10) / 10 : Math.round(value);

  const fromCm = (unit, cm) => (unit === 'imperial' ? cm / CM_PER_INCH : cm);

  const areaDisp = (unit, m2) => (unit === 'imperial' ? m2 * FT2_PER_M2 : m2);

  const formatPrice = (gen, amountMajor) => {
    if (!Number.isFinite(amountMajor) || amountMajor <= 0) return '—';
    try {
      return new Intl.NumberFormat(gen.locale, {
        style: 'currency',
        currency: gen.currencyIso,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amountMajor);
    } catch {
      return `${gen.currencyFallback || ''}${Math.round(amountMajor).toLocaleString(gen.locale)}`;
    }
  };

  class DesignSaveController {
    /** @param {any} generator */
    constructor(generator) {
      this.gen = generator;
      this.root = generator.root;
      this.enabled = this.root.dataset.enableDesignSave !== 'false';
      this.maxCompare = Math.max(2, parseInt(this.root.dataset.designSaveMaxCompare || '4', 10) || 4);
      this.iwishPageUrl = this.root.dataset.iwishPageUrl || '/apps/iwish';

      this.saveActions = this.root.querySelector('[data-ai-save-actions]');
      this.listBtn = this.root.querySelector('[data-ai-save-design="list"]');
      this.compareBtn = this.root.querySelector('[data-ai-save-design="compare"]');
      this.libraryOpenBtn = this.root.querySelector('[data-ai-open-design-library]');
      this.savedCountEl = this.root.querySelector('[data-ai-saved-count]');
      this.hintEl = this.root.querySelector('[data-ai-save-hint]');
      this.tradeBadge = this.root.querySelector('[data-ai-trade-badge]');
      this.iwishBridge = this.root.querySelector('[data-ai-iwish-bridge]');

      this.libraryDialog = this.root.querySelector('[data-ai-design-library]');
      this.libraryList = this.root.querySelector('[data-ai-design-library-list]');
      this.libraryEmpty = this.root.querySelector('[data-ai-design-library-empty]');
      this.libraryClose = this.root.querySelector('[data-ai-design-library-close]');

      this.compareDialog = this.root.querySelector('[data-ai-design-compare]');
      this.compareGrid = this.root.querySelector('[data-ai-design-compare-grid]');
      this.compareEmpty = this.root.querySelector('[data-ai-design-compare-empty]');
      this.compareClose = this.root.querySelector('[data-ai-design-compare-close]');

      if (!this.enabled || !this.saveActions) return;

      this.#bindEvents();
      this.#syncButtons();
      this.#syncTradeBadge();
    }

    #bindEvents() {
      this.listBtn?.addEventListener('click', () => this.saveToList());
      this.compareBtn?.addEventListener('click', () => this.toggleCompare());
      this.libraryOpenBtn?.addEventListener('click', () => this.openLibrary());
      this.libraryClose?.addEventListener('click', () => this.libraryDialog?.close());
      this.compareClose?.addEventListener('click', () => this.compareDialog?.close());

      this.libraryDialog?.addEventListener('click', (e) => {
        if (e.target === this.libraryDialog) this.libraryDialog.close();
      });
      this.compareDialog?.addEventListener('click', (e) => {
        if (e.target === this.compareDialog) this.compareDialog.close();
      });

      this.libraryList?.addEventListener('click', (e) => {
        const restore = /** @type {HTMLElement | null} */ (
          e.target.closest('[data-ai-restore-design]')
        );
        const remove = /** @type {HTMLElement | null} */ (
          e.target.closest('[data-ai-remove-design]')
        );
        if (restore) {
          void this.restoreDesign(restore.dataset.aiRestoreDesign);
          this.libraryDialog?.close();
        } else if (remove) {
          this.removeDesign(remove.dataset.aiRemoveDesign);
        }
      });

      this.compareGrid?.addEventListener('click', (e) => {
        const remove = /** @type {HTMLElement | null} */ (
          e.target.closest('[data-ai-remove-compare]')
        );
        if (remove) {
          this.removeFromCompare(remove.dataset.aiRemoveCompare);
          this.renderCompareDialog();
        }
      });
    }

    getLibrary() {
      return readJson(LIBRARY_KEY, []);
    }

    setLibrary(items) {
      writeJson(LIBRARY_KEY, items);
    }

    getCompareIds() {
      return readJson(COMPARE_KEY, []);
    }

    setCompareIds(ids) {
      writeJson(COMPARE_KEY, ids);
    }

    /** @param {ReturnType<DesignSaveController['gen']['calculate']>} [state] */
    onCalculate(state) {
      if (!this.enabled) return;
      this.#syncTradeBadge();
      this.#syncButtons(state);
    }

    #syncTradeBadge() {
      if (!this.tradeBadge) return;
      const active = this.gen.memberActive === true || this.gen.tradeActive === true;
      this.tradeBadge.hidden = !active;
      if (active && this.gen.memberPricingLabel) {
        this.tradeBadge.textContent = this.gen.memberPricingLabel;
      }
    }

    /** @param {ReturnType<DesignSaveController['gen']['calculate']>} [state] */
    #syncButtons(state = this.gen.measureState()) {
      const hasDesign = Boolean(this.gen.currentAsset);
      const hasMeasures =
        !this.gen.isWallShape || (state.widthDisp > 0 && state.heightDisp > 0);
      const ready = hasDesign && hasMeasures;

      if (this.listBtn) this.listBtn.disabled = !ready;
      if (this.compareBtn) this.compareBtn.disabled = !ready;

      const library = this.getLibrary();
      const compareIds = this.getCompareIds();
      const currentId = this.#currentDesignId();

      if (this.listBtn) {
        const saved = library.some((d) => d.signature === this.#signature());
        this.listBtn.classList.toggle('is-added', saved);
        this.listBtn.setAttribute('aria-pressed', String(saved));
        const label =
          this.listBtn.dataset.aiSaveListLabel ||
          this.root.dataset.designSaveListLabel ||
          'Add to List';
        const savedLabel = this.listBtn.dataset.aiSaveListSavedLabel || 'Added To List';
        const text = this.listBtn.querySelector('[data-ai-save-list-label]');
        if (text) text.textContent = saved ? savedLabel : label;
      }

      if (this.compareBtn) {
        const inCompare = compareIds.includes(currentId);
        this.compareBtn.classList.toggle('is-added', inCompare);
        this.compareBtn.setAttribute('aria-pressed', String(inCompare));
        const label = this.root.dataset.designSaveCompareLabel || 'Compare';
        const text = this.compareBtn.querySelector('[data-ai-save-compare-label]');
        if (text) {
          text.textContent = inCompare
            ? `In compare (${compareIds.length}/${this.maxCompare})`
            : label;
        }
      }

      if (this.savedCountEl) this.savedCountEl.textContent = String(library.length);
      if (this.libraryOpenBtn) this.libraryOpenBtn.hidden = library.length === 0;
    }

    #signature() {
      const asset = this.gen.currentAsset;
      const state = this.gen.measureState();
      return [
        asset?.aiImageId || '',
        asset?.source || '',
        state.widthCm,
        state.heightCm,
        state.bleedCm,
        this.gen.unit,
      ].join('|');
    }

    #currentDesignId() {
      const lib = this.getLibrary();
      const sig = this.#signature();
      const hit = lib.find((d) => d.signature === sig);
      return hit?.id || sig;
    }

    #previewUrl() {
      const img = this.gen.image;
      if (img?.src && !img.hidden) return img.src;
      const asset = this.gen.currentAsset;
      return asset?.previewUrl || asset?.watermarkedUrl || '';
    }

    buildSnapshot() {
      const gen = this.gen;
      const state = gen.measureState();
      const asset = gen.currentAsset;
      const lu = state.lengthUnit;
      const wallSize = `${state.widthDisp} × ${state.heightDisp} ${lu}`;
      const printSize = `${roundDisp(gen.unit, fromCm(gen.unit, state.printWidthCm))} × ${roundDisp(gen.unit, fromCm(gen.unit, state.printHeightCm))} ${lu}`;
      const areaLabel = `${areaDisp(gen.unit, state.actualAreaM2).toFixed(2)} ${gen.areaUnit}`;
      const priceLabel = formatPrice(gen, state.price);
      const sourceLabel =
        asset?.source === 'uploaded' ? 'Uploaded design' : 'AI generated design';
      const promptBit =
        asset?.source === 'generated' && asset.prompt
          ? ` — ${asset.prompt.slice(0, 48)}${asset.prompt.length > 48 ? '…' : ''}`
          : '';

      const properties = {
        _ai_image_id: asset?.aiImageId || '',
        _ai_source: asset?.source || '',
        _ai_seed: String(asset?.seed ?? ''),
        'Design source': asset?.source === 'uploaded' ? 'Customer upload' : 'AI generated',
        'Wall Size': wallSize,
        'Print Size': printSize,
        Area: areaLabel,
        'Panels': `${state.panels} ±`,
        Installation: state.install ? 'Yes' : 'No',
        'Saved preview': 'Watermarked — for client presentation',
      };
      if (asset?.source === 'generated' && asset.prompt) {
        properties['AI Prompt'] = asset.prompt;
      }
      if (gen.memberActive || gen.tradeActive) properties['Pricing'] = 'Appstle member';

      const sig = this.#signature();
      const existing = this.getLibrary().find((d) => d.signature === sig);

      return {
        id: existing?.id || newId(),
        signature: sig,
        savedAt: new Date().toISOString(),
        label: `${sourceLabel}${promptBit}`,
        wishlistTitle: `${gen.productTitle || 'Wallpaper'} — ${wallSize} — ${printSize}`,
        previewUrl: this.#previewUrl(),
        productId: gen.productId || '',
        variantId: gen.variantId || '',
        tradePricing: gen.memberActive === true || gen.tradeActive === true,
        priceCents: state.price,
        priceLabel,
        unit: gen.unit,
        areaUnit: gen.areaUnit,
        measurements: {
          wallSize,
          printSize,
          area: areaLabel,
          panels: state.panels,
          widthCm: Math.round(state.widthCm),
          heightCm: Math.round(state.heightCm),
          printWidthCm: Math.round(state.printWidthCm),
          printHeightCm: Math.round(state.printHeightCm),
          install: state.install,
          installRegion: state.installRegion,
        },
        asset: {
          aiImageId: asset?.aiImageId,
          source: asset?.source,
          seed: asset?.seed,
          prompt: asset?.prompt,
          model: asset?.model,
          fileName: asset?.fileName,
        },
        cartProperties: properties,
      };
    }

    saveToList() {
      if (!this.gen.currentAsset) {
        this.#hint('Create or upload a design first.');
        return;
      }
      const state = this.gen.measureState();
      if (this.gen.isWallShape && (!state.widthDisp || !state.heightDisp)) {
        this.#hint('Enter wall measurements before saving.');
        return;
      }

      const snapshot = this.buildSnapshot();
      const library = this.getLibrary().filter((d) => d.signature !== snapshot.signature);
      library.unshift(snapshot);
      this.setLibrary(library.slice(0, 40));

      this.#tryWishlistAdd(snapshot);
      this.#syncButtons(state);
      this.#hint(
        `Saved to your design list${this.iwishBridge ? ' and wishlist' : ''}. Watermarked preview with ${snapshot.measurements.wallSize} measurements.`
      );
    }

    #tryWishlistAdd(snapshot) {
      if (!this.iwishBridge || !snapshot.variantId) return;

      this.iwishBridge.dataset.variant = snapshot.variantId;
      this.iwishBridge.dataset.product = snapshot.productId;
      this.iwishBridge.dataset.pTitle = snapshot.wishlistTitle;

      try {
        if (typeof iWish !== 'undefined') {
          if (typeof iWish.addToWishlist === 'function') {
            iWish.addToWishlist({
              vid: snapshot.variantId,
              pid: snapshot.productId,
              pTitle: snapshot.wishlistTitle,
              image: snapshot.previewUrl,
            });
          } else if (typeof iWish.add === 'function') {
            iWish.add(snapshot.variantId, snapshot.productId);
          } else {
            this.iwishBridge.click();
          }
          if (typeof iWishAddFn === 'function') iWishAddFn(snapshot.variantId);
          if (typeof iWishCounter === 'function') iWishCounter();
        } else {
          this.iwishBridge.click();
        }
      } catch {
        this.iwishBridge.click();
      }
    }

    toggleCompare() {
      if (!this.gen.currentAsset) return;
      const snapshot = this.buildSnapshot();
      let ids = this.getCompareIds();
      const lib = this.getLibrary();
      if (!lib.some((d) => d.signature === snapshot.signature)) {
        const nextLib = [snapshot, ...lib.filter((d) => d.signature !== snapshot.signature)];
        this.setLibrary(nextLib.slice(0, 40));
      }

      const compareKey = snapshot.signature;
      if (ids.includes(compareKey)) {
        ids = ids.filter((x) => x !== compareKey);
      } else {
        if (ids.length >= this.maxCompare) {
          this.#hint(`Compare holds up to ${this.maxCompare} designs. Remove one to add another.`);
          this.openCompare();
          return;
        }
        ids = [...ids, compareKey];
      }
      this.setCompareIds(ids);
      this.#syncButtons();
      this.openCompare();
    }

    removeFromCompare(id) {
      this.setCompareIds(this.getCompareIds().filter((x) => x !== id));
      this.#syncButtons();
    }

    removeDesign(id) {
      this.setLibrary(this.getLibrary().filter((d) => d.id !== id));
      this.setCompareIds(this.getCompareIds().filter((x) => x !== id));
      this.renderLibrary();
      this.#syncButtons();
    }

    async restoreDesign(id) {
      const item = this.getLibrary().find((d) => d.id === id);
      if (!item) return;

      if (item.unit === 'imperial' || item.unit === 'metric') {
        this.gen.setUnit(item.unit);
      }
      if (this.gen.widthInput && item.measurements?.widthCm) {
        this.gen.widthInput.value = String(
          roundDisp(this.gen.unit, fromCm(this.gen.unit, item.measurements.widthCm))
        );
      }
      if (this.gen.heightInput && item.measurements?.heightCm) {
        this.gen.heightInput.value = String(
          roundDisp(this.gen.unit, fromCm(this.gen.unit, item.measurements.heightCm))
        );
      }

      this.gen.calculate();

      const asset = item.asset || {};
      const aiImageId = asset.aiImageId || item.cartProperties?._ai_image_id || '';
      if (item.previewUrl && aiImageId && asset.source === 'generated') {
        const restored = await this.gen.restoreGenerationHistory?.({
          ai_image_id: aiImageId,
          id: aiImageId,
          prompt: asset.prompt || '',
          preview_url: item.previewUrl,
          original_url: item.previewUrl,
          seed: asset.seed,
          model: asset.model,
          metadata: {},
        });
        if (restored) {
          this.#hint('Design and measurements restored.');
          return;
        }
      }

      if (item.previewUrl && this.gen.image) {
        this.gen.image.src = item.previewUrl;
        this.gen.image.hidden = false;
        if (this.gen.placeholder) this.gen.placeholder.hidden = true;
        if (this.gen.preview) this.gen.preview.classList.add('has-content');
      }
      this.#hint('Measurements restored. Generate or upload again if the preview does not load.');
    }

    openLibrary() {
      if (!this.libraryDialog) return;
      this.renderLibrary();
      if (typeof this.libraryDialog.showModal === 'function') {
        this.libraryDialog.showModal();
      } else {
        this.libraryDialog.setAttribute('open', '');
      }
    }

    openCompare() {
      if (!this.compareDialog) return;
      this.renderCompareDialog();
      if (typeof this.compareDialog.showModal === 'function') {
        this.compareDialog.showModal();
      } else {
        this.compareDialog.setAttribute('open', '');
      }
    }

    renderLibrary() {
      if (!this.libraryList) return;
      const items = this.getLibrary();
      if (this.libraryEmpty) this.libraryEmpty.hidden = items.length > 0;
      this.libraryList.innerHTML = items
        .map(
          (d) => `
        <article class="ai-generator__design-card">
          <div class="ai-generator__design-card-media">
            ${
              d.previewUrl
                ? `<img src="${this.#escape(d.previewUrl)}" alt="" loading="lazy" decoding="async">`
                : '<span class="ai-generator__design-card-placeholder">Preview</span>'
            }
          </div>
          <div class="ai-generator__design-card-body">
            <p class="ai-generator__design-card-title">${this.#escape(d.label)}</p>
            <ul class="ai-generator__design-card-meta">
              <li><strong>Wall:</strong> ${this.#escape(d.measurements?.wallSize || '—')}</li>
              <li><strong>Print:</strong> ${this.#escape(d.measurements?.printSize || '—')}</li>
              <li><strong>Area:</strong> ${this.#escape(d.measurements?.area || '—')}</li>
              <li><strong>Total:</strong> ${this.#escape(d.priceLabel || '—')}${d.tradePricing ? ' <em>(Appstle member)</em>' : ''}</li>
            </ul>
            <div class="ai-generator__design-card-actions">
              <button type="button" class="ai-generator__save-btn ai-generator__save-btn--ghost" data-ai-restore-design="${this.#escape(d.id)}">Restore</button>
              <button type="button" class="ai-generator__save-btn ai-generator__save-btn--ghost" data-ai-remove-design="${this.#escape(d.id)}">Remove</button>
            </div>
          </div>
        </article>`
        )
        .join('');
    }

    renderCompareDialog() {
      if (!this.compareGrid) return;
      const ids = this.getCompareIds();
      const lib = this.getLibrary();
      const items = ids
        .map((id) => lib.find((d) => d.signature === id || d.id === id))
        .filter(Boolean);

      if (this.compareEmpty) this.compareEmpty.hidden = items.length > 0;
      this.compareGrid.innerHTML = items
        .map(
          (d) => `
        <article class="ai-generator__design-compare-card">
          <div class="ai-generator__design-card-media">
            ${
              d.previewUrl
                ? `<img src="${this.#escape(d.previewUrl)}" alt="" loading="lazy" decoding="async">`
                : ''
            }
          </div>
          <p class="ai-generator__design-card-title">${this.#escape(d.label)}</p>
          <p class="ai-generator__design-compare-size">${this.#escape(d.measurements?.wallSize || '')}</p>
          <p class="ai-generator__design-compare-price">${this.#escape(d.priceLabel || '—')}${d.tradePricing ? ' · Appstle member' : ''}</p>
          <button type="button" class="ai-generator__save-btn ai-generator__save-btn--ghost" data-ai-remove-compare="${this.#escape(d.signature || d.id)}">Remove</button>
        </article>`
        )
        .join('');
    }

    #hint(text) {
      if (!this.hintEl) return;
      this.hintEl.hidden = !text;
      this.hintEl.textContent = text || '';
      if (text) {
        clearTimeout(this._hintTimer);
        this._hintTimer = window.setTimeout(() => {
          if (this.hintEl) {
            this.hintEl.hidden = true;
            this.hintEl.textContent = '';
          }
        }, 6000);
      }
    }

    #escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
    }
  }

  const attach = () => {
    document.querySelectorAll('[data-ai-generator]').forEach((root) => {
      const gen = root.wallprintAiGenerator;
      if (!gen || root.wallprintAiDesignSave) return;
      root.wallprintAiDesignSave = new DesignSaveController(gen);
    });
  };

  document.addEventListener('wallprint:ai-generator-ready', attach);

  const scheduleAttach = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(attach, { timeout: 800 });
    } else {
      window.setTimeout(attach, 50);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAttach);
  } else {
    scheduleAttach();
  }

  document.addEventListener('shopify:section:load', attach);
})();
